import clsx from "clsx";
import { Fragment, type FC } from "react";
import type { Simulation } from "rujira.js";
import { Icons } from "rujira.ui";
import type { V2PrivateFeeQuote } from "../../redacted/v2/fees";
import { privateFeeQuoteFromSimulation } from "../../redacted/v2/reviewedSimulation";
import { usePreloadedMetadataAssets } from "./Balance";
import {
  combinedPrivateFees,
  privateFeeDisplayParts,
  type FeeAssetMetadata,
} from "./privateFeeAmounts";
import { PrivateFeeDestination } from "./PrivateFeePlacement";
import { usePrivateFeeMetadata } from "./usePrivateFeeMetadata";
import "./PrivateFeeReview.scss";

/** Model B: the Reserve funding of the one-time sub is part of the fee. */
const reviewFees = (quote: V2PrivateFeeQuote) =>
  combinedPrivateFees(
    [
      ...quote.protocolFees,
      ...(quote.funding ?? []).flatMap((leg) => [
        { denom: leg.denom, amount: leg.fee },
        ...(leg.relayerFee
          ? [{ denom: leg.denom, amount: leg.relayerFee }]
          : []),
      ]),
    ],
    quote.relayerFee
  );

export const PrivateFeeSummary: FC<{
  quote: V2PrivateFeeQuote;
  assets: readonly FeeAssetMetadata[];
}> = ({ quote, assets }) => {
  const combined = reviewFees(quote);
  const amounts = combined.map((coin) => ({
    denom: coin.denom,
    ...privateFeeDisplayParts(coin, assets),
  }));
  const exact = amounts.map((amount) => amount.exact).join(" + ");
  const compact = amounts.map((amount) => amount.compact).join(" + ");
  const shortened = compact !== exact;
  const hint = combined.length
    ? "Fee for this private transaction."
    : "No privacy fee for this transaction.";
  const amountContent = amounts.map((amount, index) => (
    <Fragment key={amount.denom}>
      {index > 0 ? " + " : ""}
      {amount.amount}
      {amount.symbol && (
        <>
          {" "}
          <span className="color-grey">{amount.symbol}</span>
        </>
      )}
    </Fragment>
  ));
  return (
    <div
      className="private-fee-review flex ai-c jc-sb gap-1 w-full condensed color-grey"
      style={{ flexBasis: "100%", minWidth: 0 }}
      aria-label="Private transaction fee review">
      <button
        type="button"
        className="private-fee-trigger iflex ai-c fw-500"
        aria-label="Privacy fee details"
        onClick={(event) => event.currentTarget.focus()}
        onKeyDown={(event) => {
          if (event.key === "Escape") event.currentTarget.blur();
        }}
        data-tooltip-id="global-tip"
        data-tooltip-class-name="private-fee-tooltip"
        data-tooltip-content={hint}>
        Privacy fee
        <Icons.Info className="ml-0.5 block w-2.5" />
      </button>
      <span
        // Amounts use the number font of the other fee rows; "No charge"
        // stays text like "Instant".
        className={clsx("color-white text-right", {
          decimal: combined.length > 0,
        })}
        style={{ overflowWrap: "anywhere", minWidth: 0 }}>
        {shortened ? (
          <button
            type="button"
            className="private-fee-trigger"
            aria-label={`Exact privacy fee: ${exact}`}
            onClick={(event) => event.currentTarget.focus()}
            onKeyDown={(event) => {
              if (event.key === "Escape") event.currentTarget.blur();
            }}
            data-tooltip-id="global-tip"
            data-tooltip-class-name="private-fee-tooltip"
            data-tooltip-content={exact}>
            {amountContent}
          </button>
        ) : combined.length ? (
          amountContent
        ) : (
          "No charge"
        )}
      </span>
    </div>
  );
};

/** Supplements account metadata with public denomination metadata only. */
type PrivateFeeReviewProps = {
  simulation?: Simulation;
  error?: Error;
  isPaused?: boolean;
};
const PrivateFeeReviewContent: FC<PrivateFeeReviewProps> = ({
  simulation,
  error,
}) => {
  const preloadedAssets = usePreloadedMetadataAssets();
  const quote = privateFeeQuoteFromSimulation(simulation);
  const assets = usePrivateFeeMetadata(
    quote ? reviewFees(quote) : [],
    preloadedAssets
  );
  if (error)
    return (
      <p
        className="private-fee-review block w-full mb-2 fs-12 color-orange text-center"
        style={{ flexBasis: "100%", overflowWrap: "anywhere" }}
        role="alert">
        {error.message}
      </p>
    );
  if (!quote)
    return (
      <p
        className="private-fee-review block w-full mb-2 fs-12 color-grey text-center"
        style={{ flexBasis: "100%" }}
        role="status">
        Calculating privacy fees…
      </p>
    );
  return <PrivateFeeSummary quote={quote} assets={assets} />;
};

export const PrivateFeeReview: FC<PrivateFeeReviewProps> = (props) =>
  // A running transaction keeps the fee it was confirmed with until it is
  // done or failed. Any other pause leaves no empty pending row: the button
  // and the toast already show the progress.
  props.isPaused &&
  (props.error || !privateFeeQuoteFromSimulation(props.simulation)) ? null : (
    <PrivateFeeDestination>
      <PrivateFeeReviewContent {...props} />
    </PrivateFeeDestination>
  );
